import { Box, Tooltip, Typography } from '@mui/material';
import Toaster from '@shared/components/ui/Toaster';
import { getLabel, getMessage } from '@shared/utils';

import { useOptionalInterruptedCashinSession } from './InterruptedCashinProvider';
import {
  CASHIN_FADE_MS,
  type CashinBannerStatus,
  type CashinResolvedStatus,
  type FollowedCashin,
} from './useInterruptedCashin';

const resolvedMessage = (status: CashinResolvedStatus): string =>
  getMessage(status === 'completed' ? 'cashinInterruptedCompletedTitle' : 'cashinVerifiedRejectedTitle');

const bannerMessage = (status: CashinBannerStatus): string =>
  status === 'tracking' ? getMessage('cashinInProgressBanner') : resolvedMessage(status);

const formatAmount = (amount: string): string => {
  if (!amount) {
    return '';
  }
  return amount.startsWith('Bs') ? amount : `Bs ${amount}`;
};

export const cashinBannerTooltip = (followed: FollowedCashin | null): string => {
  if (!followed) {
    return '';
  }
  const lines = [
    followed.accountOwner ? `${getLabel('ownerName')}: ${followed.accountOwner}` : '',
    followed.cellphone ? `${getLabel('cellphone')}: ${followed.cellphone}` : '',
    followed.amount ? `${getLabel('amount')}: ${formatAmount(followed.amount)}` : '',
  ].filter(Boolean);
  return lines.join('\n');
};

export const CashinProgressBanner = () => {
  const session = useOptionalInterruptedCashinSession();
  if (!session) {
    return null;
  }

  const { banner, toaster, closeToaster, followed } = session;
  const tooltip = cashinBannerTooltip(followed);

  return (
    <>
      {banner ? (
        <Tooltip
          title={
            tooltip ? (
              <Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block' }}>
                {tooltip}
              </Box>
            ) : (
              ''
            )
          }
          disableHoverListener={!tooltip}
          arrow
          enterTouchDelay={0}>
          <Typography
            component="output"
            className={
              banner.status === 'tracking'
                ? 'cashin-progress-label cashin-progress-label--live'
                : 'cashin-progress-label'
            }
            aria-live="polite"
            tabIndex={tooltip ? 0 : undefined}
            sx={{
              opacity: banner.fading ? 0 : 1,
              transition: `opacity ${CASHIN_FADE_MS}ms ease`,
              cursor: tooltip ? 'help' : 'default',
            }}>
            {bannerMessage(banner.status)}
          </Typography>
        </Tooltip>
      ) : null}
      <Toaster
        type={toaster.type}
        open={toaster.open}
        handleClose={closeToaster}
        message={resolvedMessage(toaster.status)}
      />
    </>
  );
};
